import { Component } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BuilderLocalOptions,
  BuilderOptions,
  GetBuilderOptionsRequest,
  BuilderExportOptions,
  OpenDoorsConfig,
  SituViewWebApiClient,
  UpdateBuilderOptionsRequest
} from 'situview-webapi-client';
import { AuthService, ConfirmDialogService, StateService } from 'src/app/services';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

/**
 * One editable row on the page. `key` is the field on BuilderExportOptions (or BuilderLocalOptions
 * when `local` is set); the label and help are the builder-facing wording from the v3 mockup
 * (WarpCore/Documentation/mockups/feedback-page-v3.html).
 *
 * `invert` is for the UseIfc, Skip and Dont flags: the contract asks "use the cornice drawn in the
 * file?", the builder is asked "generate cornice?", so the switch shows the opposite of the
 * stored value.
 */
export interface BuilderOptionRow {
  key: string;
  label: string;
  help?: string;
  kind: 'toggle' | 'select' | 'number';
  invert?: boolean;
  unit?: string;
  step?: number;
  min?: number;
  max?: number;
  choices?: { value: number; label: string }[];
  local?: boolean;
  /** Key of a row this one only applies under; disabled while that row is off (see offValue for selects). */
  dependsOn?: string;
  /** For a select row that others depend on: the choice that counts as off. */
  offValue?: number;
}

export interface BuilderOptionGroup {
  title: string;
  rows: BuilderOptionRow[];
}

/**
 * Mirror of BuilderOptions.ColdStartExportOptions() and the BuilderLocalOptions initialisers in
 * WarpCore.Contracts/BuilderOptions.cs. The API returns these values when a builder has never
 * saved, but does not expose them alongside a saved record, so the page carries a copy for
 * "Reset all options to defaults". Keep in step with the C#.
 */
export function coldStartExportOptions(): BuilderExportOptions {
  return new BuilderExportOptions({
    facadeView: false,
    openDoors: OpenDoorsConfig.All,
    openDoorsWithOnlyOneRoomAdjoining: true,
    replaceGarageDoor: true,
    installDoorsAndWindowsFromOptions: true,
    useIfcCornice: false,
    useIfcSkirting: false,
    useIfcRidgeCapping: false,
    useIfcBargeCapping: true,
    skirtingEndSetback: 0.01,
    skirtingReturnedEnds: true,
    corniceEndSetback: 0.01,
    corniceReturnedEnds: true,
    dontGenerateMeshesForWindowOpenings: false,
    garageOptionsMissingCeiling: true,
    mergeFloorplans: true,
    skipFenceGeneration: false,
    homeVerticalAdjustment: 0,
    adjustOrientationOfFlippedProducts: true,
    porchSlabAddedHeight: 95,
    addLightsToRobes: true,
    archicadCabinets: true,
    extraInteriorLights: true
  });
}

export function coldStartLocalOptions(): BuilderLocalOptions {
  return new BuilderLocalOptions({ revealMaterialSplit: 0.5 });
}

export function coldStartBuilderOptions(): BuilderOptions {
  return new BuilderOptions({
    exportOptions: coldStartExportOptions(),
    localOptions: coldStartLocalOptions()
  });
}

export const EXPORT_OPTION_GROUPS: BuilderOptionGroup[] = [
  {
    title: 'Doors',
    rows: [
      {
        key: 'openDoors', kind: 'select', offValue: OpenDoorsConfig.None, label: 'Which doors open in the viewer',
        help: 'Your drawings set the swing and the hand; this decides which doors are animated at all.',
        choices: [
          { value: OpenDoorsConfig.All, label: 'All doors' },
          { value: OpenDoorsConfig.NoLinen, label: 'All except linen' },
          { value: OpenDoorsConfig.NoGarageNoLinen, label: 'All except garage and linen' },
          { value: OpenDoorsConfig.None, label: 'None' }
        ]
      },
      {
        key: 'openDoorsWithOnlyOneRoomAdjoining', kind: 'toggle', dependsOn: 'openDoors', label: 'Open a door even when only one room adjoins it',
        help: 'Usually an external door with only one room adjoining is a garage or a store.'
      },
      { key: 'replaceGarageDoor', kind: 'toggle', label: 'Replace the drawn garage door with a Situ product' },
      {
        key: 'installDoorsAndWindowsFromOptions', kind: 'toggle', label: 'Carry doors and windows from option files onto the master',
        help: 'For facade files drawn as carrier walls.'
      }
    ]
  },
  {
    title: 'Cornice and skirting',
    rows: [
      {
        key: 'useIfcCornice', kind: 'toggle', invert: true, label: 'Generate cornice',
        help: 'Off means the viewer uses whatever cornice is drawn in your file.'
      },
      { key: 'corniceReturnedEnds', kind: 'toggle', dependsOn: 'useIfcCornice', label: 'Return cornice ends at a doorway' },
      {
        key: 'corniceEndSetback', kind: 'number', dependsOn: 'corniceReturnedEnds', unit: 'm', step: 0.005, min: 0, label: 'Cornice return setback',
        help: 'Setback from the opening, in metres.'
      },
      {
        key: 'useIfcSkirting', kind: 'toggle', invert: true, label: 'Generate skirting',
        help: 'Off means the viewer uses whatever skirting is drawn in your file.'
      },
      { key: 'skirtingReturnedEnds', kind: 'toggle', dependsOn: 'useIfcSkirting', label: 'Return skirting ends at a doorway' },
      {
        key: 'skirtingEndSetback', kind: 'number', dependsOn: 'skirtingReturnedEnds', unit: 'm', step: 0.005, min: 0, label: 'Skirting return setback',
        help: 'Setback from the opening, in metres.'
      },
      { key: 'useIfcRidgeCapping', kind: 'toggle', invert: true, label: 'Generate ridge capping' },
      { key: 'useIfcBargeCapping', kind: 'toggle', invert: true, label: 'Generate barge capping' }
    ]
  },
  {
    title: 'Lighting',
    rows: [
      {
        key: 'extraInteriorLights', kind: 'toggle', label: 'Extra interior lights',
        help: 'More downlights than the room count alone would give. Cannot be removed one by one in situFURNISH.'
      },
      { key: 'addLightsToRobes', kind: 'toggle', label: 'Light the walk-in robes' }
    ]
  },
  {
    title: 'Facade and plans',
    rows: [
      { key: 'facadeView', kind: 'toggle', label: 'Three extra viewpoints facing the facade' },
      { key: 'mergeFloorplans', kind: 'toggle', label: 'Merge the floorplans of master and options into one' },
      {
        key: 'porchSlabAddedHeight', kind: 'number', unit: 'mm', step: 1, min: 0, label: 'Raise the porch slab in facade renders',
        help: 'A few millimetres stops the slab flickering against the ground.'
      },
      {
        key: 'homeVerticalAdjustment', kind: 'number', unit: 'm', step: 0.01, label: 'Lift or lower the whole home',
        help: 'Metres, when the file\'s ground level is not the site\'s.'
      },
      { key: 'skipFenceGeneration', kind: 'toggle', invert: true, label: 'Generate fences' },
      { key: 'garageOptionsMissingCeiling', kind: 'toggle', label: 'Treat a garage in an option file as having no ceiling' }
    ]
  },
  {
    title: 'Products',
    rows: [
      {
        key: 'adjustOrientationOfFlippedProducts', kind: 'toggle', label: 'Correct the hand of mirrored products',
        help: 'A flipped family in the file gets its replacement product flipped to match.'
      },
      {
        key: 'archicadCabinets', kind: 'toggle', label: 'Cabinets are drawn the ArchiCAD way',
        help: 'Turn off for Revit exports.'
      },
      { key: 'dontGenerateMeshesForWindowOpenings', kind: 'toggle', invert: true, label: 'Generate meshes for window openings' }
    ]
  }
];

/** Staff-facing: consumed while the warp module is generated, never reaches the viewer. */
export const LOCAL_OPTION_GROUPS: BuilderOptionGroup[] = [
  {
    title: 'Generation settings',
    rows: [
      {
        key: 'revealMaterialSplit', kind: 'number', local: true, step: 0.05, min: 0, max: 1, label: 'Reveal material split',
        help: 'Where the interior/exterior material junction falls across a wall\'s thickness, as a fraction. ' +
          'For example 0.9 reads as 90% brick, 10% plasterboard. ' +
          'A visual preference, not the true layer interface.'
      }
    ]
  }
];

@Component({
  selector: 'app-builder-options',
  templateUrl: './builder-options.component.html',
  styleUrls: ['./builder-options.component.less']
})
export class BuilderOptionsComponent extends SituBaseComponent {
  loading = false;
  saving = false;
  loadError: string | undefined;

  /** The record being edited. */
  options: BuilderOptions | undefined;
  /** What the server last gave us, to answer "has anything changed?" */
  private loaded: BuilderOptions | undefined;
  /** Passed back on save so we never overwrite a version saved since the page loaded. */
  private latestArtifactId: string | undefined;

  readonly defaults = coldStartBuilderOptions();
  readonly exportGroups = EXPORT_OPTION_GROUPS;
  readonly localGroups = LOCAL_OPTION_GROUPS;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private dialogService: ConfirmDialogService,
    private situViewApiClient: SituViewWebApiClient
  ) {
    super(authService, stateService);
  }

  protected onSelectedCompanyChange(): void {
    this.loadData();
  }

  get isDirty(): boolean {
    if (!this.options || !this.loaded) {
      return false;
    }
    return JSON.stringify(this.options.toJSON()) !== JSON.stringify(this.loaded.toJSON());
  }

  loadData(): void {
    if (!this.selectedCompany?.id) {
      return;
    }

    this.loading = true;
    this.loadError = undefined;
    this.options = undefined;
    this.loaded = undefined;

    const request = new GetBuilderOptionsRequest({ companyId: this.selectedCompany.id });

    this.situViewApiClient.getBuilderOptions(request)
      .then(response => {
        if (response.success && response.options) {
          this.accept(response.options, response.artifactId);
        } else {
          this.loadError = response.errorMessages?.[0] ?? 'Failed to load builder options';
        }
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
        this.loadError = 'Could not retrieve builder options';
      });
  }

  save(): void {
    if (!this.options || !this.selectedCompany?.id || this.saving) {
      return;
    }

    this.saving = true;

    const request = new UpdateBuilderOptionsRequest({
      companyId: this.selectedCompany.id,
      options: this.options,
      latestArtifactId: this.latestArtifactId
    });

    this.situViewApiClient.updateBuilderOptions(request)
      .then(response => {
        if (response.success && response.options) {
          this.accept(response.options, response.artifactId);
          this.showMessage('Builder options saved');
        } else {
          this.showMessage(response.errorMessages?.[0] ?? 'Failed to save builder options');
        }
        this.saving = false;
      })
      .catch(() => {
        this.saving = false;
        this.showMessage('Could not save builder options');
      });
  }

  /** Discard unsaved edits and go back to what the server holds. */
  revert(): void {
    if (this.loaded) {
      this.options = this.clone(this.loaded);
    }
  }

  /** Put every row back to its default; nothing is saved until the builder chooses to. */
  resetToDefaults(): void {
    const data = new ConfirmDialogData();
    data.title = 'Reset all options to defaults';
    data.message = 'Every option on this page will be set back to its default. Nothing is saved until you press Save.';
    data.confirmText = 'Reset';
    data.cancelText = 'Cancel';
    data.actionIcon = 'restart_alt';
    data.actionColour = 'primary';

    this.dialogService.open(data);
    this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.options = this.clone(this.defaults);
      }
    });
  }

  // ---- row access -------------------------------------------------------------------------

  private target(row: BuilderOptionRow, options: BuilderOptions | undefined): any {
    if (!options) {
      return undefined;
    }
    return row.local ? options.localOptions : options.exportOptions;
  }

  getValue(row: BuilderOptionRow): any {
    return this.target(row, this.options)?.[row.key];
  }

  setValue(row: BuilderOptionRow, value: any): void {
    const t = this.target(row, this.options);
    if (t) {
      t[row.key] = value;
    }
  }

/** False while any toggle this row depends on, directly or up the chain, shows off. */
  isEnabled(row: BuilderOptionRow): boolean {
    let parentKey = row.dependsOn;
    while (parentKey) {
      const parent = this.findRow(parentKey);
      if (!parent || !this.isOn(parent)) {
        return false;
      }
      parentKey = parent.dependsOn;
    }
    return true;
  }

  /** Whether a row others depend on currently counts as on. */
  private isOn(row: BuilderOptionRow): boolean {
    if (row.kind === 'select') {
      return this.getValue(row) !== row.offValue;
    }
    return this.getToggle(row);
  }

  /** How many rows sit above this row in its dependency chain, for indenting. */
  depth(row: BuilderOptionRow): number {
    let n = 0;
    let parentKey = row.dependsOn;
    while (parentKey) {
      n++;
      parentKey = this.findRow(parentKey)?.dependsOn;
    }
    return n;
  }

  private findRow(key: string): BuilderOptionRow | undefined {
    for (const group of [...this.exportGroups, ...this.localGroups]) {
      const row = group.rows.find(r => r.key === key);
      if (row) {
        return row;
      }
    }
    return undefined;
  }

  getToggle(row: BuilderOptionRow): boolean {
    const v = !!this.getValue(row);
    return row.invert ? !v : v;
  }

  setToggle(row: BuilderOptionRow, checked: boolean): void {
    this.setValue(row, row.invert ? !checked : checked);
  }

  getNumber(row: BuilderOptionRow): number | undefined {
    const v = this.getValue(row);
    return typeof v === 'number' ? v : undefined;
  }

  setNumber(row: BuilderOptionRow, raw: any): void {
    const n = typeof raw === 'number' ? raw : parseFloat(raw);
    if (!isNaN(n)) {
      this.setValue(row, n);
    }
  }

  // ---- helpers ----------------------------------------------------------------------------

  private accept(options: BuilderOptions, artifactId: string | null | undefined): void {
    // Fill any half the server left out so every row has something to bind to.
    options.exportOptions = options.exportOptions ?? coldStartExportOptions();
    options.localOptions = options.localOptions ?? coldStartLocalOptions();

    this.loaded = this.clone(options);
    this.options = this.clone(options);
    this.latestArtifactId = artifactId ?? undefined;
  }

  private clone(options: BuilderOptions): BuilderOptions {
    return BuilderOptions.fromJS(JSON.parse(JSON.stringify(options.toJSON())));
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}
